import { Component, Injector, QueryList, ViewChildren } from '@angular/core';
import { Borrower } from '../../../models/borrower-model';
import { MultiBorrowersAddressHistoryStep } from '../../../models/wizard/multiborrowers-address-history-step.model';
import { BorrowerAddressHistoryComponent } from '../../borrower-address-history/borrower-address-history.component';
import { WizardStepComponentBase } from '../wizard-step-base.component';
import * as _ from 'lodash';
import { MortgageApplication } from '../../../models/mortgage-app.model';
import { StepType } from '../../../models/wizard/config/step-type.enum';
import { ResidencyBasis } from '../../../models/enums/residency-basis.enum';
import { ResidencyAddress } from '../../../models/residency-address.model';
import { RealEstateOwned } from '../../../models/mortgage.model';
import { reoMatchesAddress } from '../../reo-address-match.util';
import { isActive } from '../../../utils/entity-state';

@Component({
    selector: 'multiborrowers-address-history',
    templateUrl: 'multiborrowers-address-history-step.component.html',
    standalone: false
})

export class MultiBorrowersAddressHistoryComponent extends WizardStepComponentBase<MultiBorrowersAddressHistoryStep> {

  @ViewChildren('borrowerAddressHistory') borrowerAddressHistoryComponents: QueryList<BorrowerAddressHistoryComponent> | undefined;

  mainBorrower!: Borrower;
  coBorrowers: Borrower[] = [];
  mortgage: MortgageApplication;

  reoFieldsToConfig: any = {};

  isStepBeingEdited: boolean = false;

  private _invalidBorrowerAddressComponent: BorrowerAddressHistoryComponent;

  constructor(private readonly _injector: Injector) {
    super(_injector);
    this.saveMortgageApplicationBeforeNextStep = true;
    this.mortgage = this.mortgageApplication;
    if (this.currentBorrower) {
      this.mainBorrower = this.currentBorrower;
      this.coBorrowers = this.mortgageApplication.borrowers.filter(b => b.primaryEmail.toLocaleLowerCase() !==
        this.mainBorrower.primaryEmail.toLocaleLowerCase());
    }

    this.isStepBeingEdited = this.wizardFlowService.isEditEnabled;

    const reoStepConfig = this.wizardFlowService.activeFlowConfig.steps.find(s => s.stepType === StepType.ReoStep);
    if (reoStepConfig) {
      const reoStep = this.wizardFlowService.getStepById(reoStepConfig.id);
      if (reoStep) {
        this.reoFieldsToConfig = reoStep.fieldConfig;
      }
    }
  }

  onNextClicked() {
    let valid = true;
    this._invalidBorrowerAddressComponent = null;
    if (this.borrowerAddressHistoryComponents) {
      for (const borrowerAddressHistoryComponent of this.borrowerAddressHistoryComponents) {
        valid = borrowerAddressHistoryComponent.validate() && valid;
        if (!valid && !this._invalidBorrowerAddressComponent) {
          this._invalidBorrowerAddressComponent = borrowerAddressHistoryComponent;
        }
      }
    }
    if (valid) {
      super.onNextClicked();
    } else {
      this._invalidBorrowerAddressComponent.scrollToFirstInvalid();
    }
  }

  onCopyFromPrimaryBorrowerClicked = (toBorrower: Borrower) => {
    // Clear existing addresses
    toBorrower.residencyAddresses = [];

    // Copy addresses from main borrower
    this.mainBorrower.residencyAddresses.forEach(residencyAddress => {
      if (residencyAddress.residencyBasis === ResidencyBasis.Own) {
        const reoForThatAddress = this.findAlreadyExistingReoInMortgage(residencyAddress);
        if (reoForThatAddress) {
          if (!reoForThatAddress.owningBorrowerIds.includes(toBorrower.borrowerId)) {
            reoForThatAddress.owningBorrowerIds.push(toBorrower.borrowerId);
          }
        }
      }
      // LLS-130: the copy is inserted as a new row, so every identity the source row owns has to be
      // dropped - both the ids and the modelGuids. Keeping a modelGuid makes the insert collide with the
      // row it was cloned from: on the residency that is a duplicate-key 500 (UX_ResidencyAddress_ModelGuid),
      // and on the nested address it silently duplicates the guid that LOS address mappings key off.
      const residencyAddressToCopy = _.cloneDeep(residencyAddress);
      residencyAddressToCopy.residencyAddressId = null;
      residencyAddressToCopy.addressId = null;
      (residencyAddressToCopy as any).modelGuid = null;
      if (residencyAddressToCopy.address) {
        residencyAddressToCopy.address.addressId = null;
        (residencyAddressToCopy.address as any).modelGuid = null;
      }
      toBorrower.residencyAddresses.push(residencyAddressToCopy);
    });

    // Find the child component for this borrower and refresh it
    const childComponent = this.borrowerAddressHistoryComponents?.find(c => c.borrower.borrowerId === toBorrower.borrowerId);
    if (childComponent) {
      childComponent.initializeAddresses();
    }
    this.cdr.detectChanges();
  }

  private findAlreadyExistingReoInMortgage = (address: ResidencyAddress): RealEstateOwned => {
    // LLS-140: match on normalized address parts so a co-borrower is linked to the primary
    // borrower's existing REO even when autocomplete/formatting reshaped the copied address,
    // instead of leaving a second REO to be created for the same property.
    return this.mortgage?.realEstateOwned.find(reo => isActive(reo) && reoMatchesAddress(reo, address.address));
  }
}
